import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { patchCompany } from '../../../../api/company/company';
import { useNotification } from '../../../../context/NotificationContext';
import { useTutorial } from '../../../tutorial/TutorialContext';
import { useTheme } from '@mui/material/styles';
import './BookingSettings/BookingSettings.css';

const BookingSettings = () => {
  const { companyData, updateCompanyData } = useAuth();
  const { markStepComplete } = useTutorial();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [allowDoubleBooking, setAllowDoubleBooking] = useState(false);
  const [maxConcurrentPerSlot, setMaxConcurrentPerSlot] = useState('');
  const [allowClientCheckIn, setAllowClientCheckIn] = useState(false);
  const [checkInWindowMinutesBefore, setCheckInWindowMinutesBefore] = useState('30');
  const [requireClientConfirmedBeforeCheckIn, setRequireClientConfirmedBeforeCheckIn] = useState(false);
  const [automatedCheckInReminderEnabled, setAutomatedCheckInReminderEnabled] = useState(false);
  const [checkInReminderSendSms, setCheckInReminderSendSms] = useState(false);
  const [checkInReminderSendEmail, setCheckInReminderSendEmail] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { showToast } = useNotification();
  const saveTimeoutRef = useRef(null);
  const isSpinnerActiveRef = useRef(false);

  const cssVars = useMemo(() => {
    const surface = isDark ? '#0f172a' : '#ffffff';
    const surface2 = isDark ? 'rgba(15, 23, 42, 0.72)' : 'rgba(255, 255, 255, 0.7)';
    const text = isDark ? '#e5e7eb' : '#0f172a';
    const muted = isDark ? '#94a3b8' : '#64748b';
    const border = isDark ? 'rgba(148, 163, 184, 0.22)' : 'rgba(15, 23, 42, 0.12)';
    const shadow = isDark ? '0 10px 30px rgba(0,0,0,0.35)' : '0 10px 30px rgba(2, 6, 23, 0.08)';
    const inputBg = isDark ? 'rgba(2, 6, 23, 0.35)' : 'rgba(255, 255, 255, 0.9)';
    const inputBgFocus = isDark ? 'rgba(2, 6, 23, 0.55)' : '#ffffff';
    const shimmerA = isDark ? 'rgba(226, 232, 240, 0.08)' : 'rgba(15, 23, 42, 0.06)';
    const shimmerB = isDark ? 'rgba(226, 232, 240, 0.14)' : 'rgba(15, 23, 42, 0.12)';

    return {
      '--bs-surface': surface,
      '--bs-surface-2': surface2,
      '--bs-text': text,
      '--bs-muted': muted,
      '--bs-border': border,
      '--bs-shadow': shadow,
      '--bs-focus': 'rgba(72, 138, 236, 0.45)',
      '--bs-input-bg': inputBg,
      '--bs-input-bg-focus': inputBgFocus,
      '--bs-shimmer-a': shimmerA,
      '--bs-shimmer-b': shimmerB
    };
  }, [isDark]);

  useEffect(() => {
    const fetchSettings = async () => {
      if (!companyData?.id) {
        setLoading(false);
        return;
      }

      try {
        const data = companyData || {};
        const currentValue = data?.settings?.allowDoubleBooking === true;
        setAllowDoubleBooking(currentValue);

        const rawMax = data?.settings?.maxConcurrentAppointmentsPerSlot;
        if (typeof rawMax === 'number' && Number.isFinite(rawMax) && rawMax > 0) {
          setMaxConcurrentPerSlot(String(rawMax));
        } else {
          setMaxConcurrentPerSlot('');
        }

        const booking = data?.settings?.bookingSettings || {};

        setAllowClientCheckIn(booking.allowClientCheckIn === true);
        const windowMins = Number(booking.checkInWindowMinutesBefore);
        setCheckInWindowMinutesBefore(
          Number.isFinite(windowMins) && windowMins > 0 ? String(windowMins) : '30'
        );
        setRequireClientConfirmedBeforeCheckIn(booking.requireClientConfirmedBeforeCheckIn === true);
        setAutomatedCheckInReminderEnabled(booking.automatedCheckInReminderEnabled === true);
        setCheckInReminderSendSms(booking.checkInReminderSendSms === true);
        setCheckInReminderSendEmail(booking.checkInReminderSendEmail === true);
      } catch (error) {
        console.error('Error loading booking settings:', error);
        showToast('Failed to load booking settings', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [companyData?.id]);

  const handleToggleChange = async (event) => {
    const nextValue = event.target.checked;
    setAllowDoubleBooking(nextValue);

    if (!companyData?.id) return;

    try {
      setSaving(true);
      const settings = { allowDoubleBooking: nextValue };

      // When enabling double booking and no cap is set yet, default to 2 for safety.
      if (nextValue) {
        const raw = (maxConcurrentPerSlot || '').trim();
        if (!raw) {
          settings.maxConcurrentAppointmentsPerSlot = 2;
          setMaxConcurrentPerSlot('2');
        }
      }

      const company = await patchCompany({
        companyId: companyData.id,
        company: { settings },
      });
      updateCompanyData?.({ ...companyData, ...company });

      showToast('Booking settings updated successfully', 'success');
      markStepComplete(5);
    } catch (error) {
      console.error('Error saving booking settings:', error);
      setAllowDoubleBooking((prev) => !prev);
      showToast('Failed to save booking settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveMaxConcurrent = async (value) => {
    if (!companyData?.id) return;

    const numericValue = value === '' ? null : Number(value);

    try {
      setSaving(true);
      const company = await patchCompany({
        companyId: companyData.id,
        company: {
          settings: {
            maxConcurrentAppointmentsPerSlot:
              numericValue && Number.isFinite(numericValue) && numericValue > 0
                ? numericValue
                : null,
          },
        },
      });
      updateCompanyData?.({ ...companyData, ...company });

      showToast('Slot capacity updated successfully', 'success');
      markStepComplete(5);
    } catch (error) {
      console.error('Error saving slot capacity:', error);
      showToast('Failed to save slot capacity', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleMaxConcurrentChange = (event) => {
    const value = event.target.value;

    // Allow empty (treated as unlimited), or positive integers
    if (value === '' || /^[0-9]+$/.test(value)) {
      setMaxConcurrentPerSlot(value);
      
      // Clear any existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Debounce the save - wait 800ms after last change
      saveTimeoutRef.current = setTimeout(() => {
        saveMaxConcurrent(value);
      }, 800);
    }
  };

  const handleInputBlur = () => {
    // Save immediately when user clicks away
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveMaxConcurrent(maxConcurrentPerSlot);
    isSpinnerActiveRef.current = false;
  };

  const handleInputMouseDown = (e) => {
    // Check if click is on spinner buttons (up/down arrows)
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    const width = rect.width;
    
    // Spinner buttons are typically on the right side of the input
    // For number inputs, spinners are usually in the last 20-30px
    if (clickX > width - 30) {
      isSpinnerActiveRef.current = true;
    }
  };

  const handleInputMouseUp = () => {
    if (isSpinnerActiveRef.current) {
      // Small delay to ensure the value has updated
      setTimeout(() => {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
        saveMaxConcurrent(maxConcurrentPerSlot);
        isSpinnerActiveRef.current = false;
      }, 100);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const saveBookingWorkflowSettings = async (patch) => {
    if (!companyData?.id) return;

    try {
      setSaving(true);
      const company = await patchCompany({
        companyId: companyData.id,
        company: { settings: { bookingSettings: patch } },
      });
      updateCompanyData?.({ ...companyData, ...company });
      showToast('Appointment settings updated', 'success');
      markStepComplete(5);
    } catch (error) {
      console.error('Error saving appointment settings:', error);
      showToast('Failed to save appointment settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAllowClientCheckInChange = async (event) => {
    const next = event.target.checked;
    setAllowClientCheckIn(next);
    await saveBookingWorkflowSettings({ allowClientCheckIn: next });
  };

  const handleRequireConfirmBeforeCheckInChange = async (event) => {
    const next = event.target.checked;
    setRequireClientConfirmedBeforeCheckIn(next);
    await saveBookingWorkflowSettings({ requireClientConfirmedBeforeCheckIn: next });
  };

  const handleCheckInWindowBlur = async () => {
    const parsed = parseInt((checkInWindowMinutesBefore || '').trim(), 10);
    const value = Number.isFinite(parsed) && parsed > 0 ? parsed : 30;
    setCheckInWindowMinutesBefore(String(value));
    await saveBookingWorkflowSettings({ checkInWindowMinutesBefore: value });
  };

  const handleAutomatedCheckInReminderChange = async (event) => {
    const next = event.target.checked;
    setAutomatedCheckInReminderEnabled(next);
    await saveBookingWorkflowSettings({ automatedCheckInReminderEnabled: next });
  };

  const handleCheckInReminderSmsChange = async (event) => {
    const next = event.target.checked;
    setCheckInReminderSendSms(next);
    await saveBookingWorkflowSettings({ checkInReminderSendSms: next });
  };

  const handleCheckInReminderEmailChange = async (event) => {
    const next = event.target.checked;
    setCheckInReminderSendEmail(next);
    await saveBookingWorkflowSettings({ checkInReminderSendEmail: next });
  };

  const getModeSummary = () => {
    if (!allowDoubleBooking) {
      return 'Current mode: No overlapping bookings (1 per staff member & time slot).';
    }

    const cap = parseInt((maxConcurrentPerSlot || '').trim(), 10);
    if (!maxConcurrentPerSlot || !Number.isFinite(cap) || cap <= 0) {
      return 'Current mode: Unlimited overlapping bookings per staff member & time slot.';
    }

    if (cap === 1) {
      return 'Current mode: Same as no double booking (1 per staff member & time slot).';
    }

    return `Current mode: Up to ${cap} bookings per staff member & time slot.`;
  };

  return (
    <div className="bookingSettingsPage" style={cssVars}>
      <div className="bookingSettingsInner">
        <div className="bookingSettingsHeader">
          <h1>Booking Settings</h1>
          <p>
            Control how strictly your schedule prevents overlapping appointments for each staff member.
            These settings apply to both client self-booking and manual bookings in the app.
          </p>
        </div>

        {loading ? (
          <div className="bsLoading" aria-busy="true" aria-live="polite">
            <div className="bsLoadingCard">
              <div className="bsShimmerLine short" />
              <div className="bsShimmerLine medium" />
              <div className="bsShimmerLine" />
              <div className="bsShimmerLine" />
            </div>
          </div>
        ) : (
          <>
          <section className="bsCard" aria-label="Booking rules">
            <div className="bsCardBody">
              <div className="bsRow">
                <div style={{ minWidth: 260, flex: 1 }}>
                  <p className="bsRowTitle">Allow Double Booking</p>
                  <p className="bsRowDesc">
                    When enabled, the system will allow overlapping appointments for the same staff
                    member or calendar. When disabled, clients can only book open time slots.
                  </p>
                </div>

                <label className="bsCheckbox" aria-label="Allow double booking">
                  <input
                    type="checkbox"
                    checked={allowDoubleBooking}
                    onChange={handleToggleChange}
                    disabled={saving}
                  />
                </label>
              </div>

              <div className="bsDivider" />

              <div className="bsField">
                <div className="bsLabelRow">
                  <label className="bsLabel" htmlFor="bs-maxConcurrent">
                    Max concurrent bookings per time slot
                  </label>
                  <span className="bsLabel">
                    {saving ? 'Saving…' : allowDoubleBooking ? 'Optional cap' : 'Locked to 1'}
                  </span>
                </div>

                <div className="bsRowDesc" style={{ marginTop: 0 }}>
                  Set how many appointments are allowed at the same time for the same staff member (not
                  for the whole company). Leave blank for unlimited when double booking is enabled. When
                  double booking is disabled, the system always enforces a limit of 1.
                </div>

                <input
                  id="bs-maxConcurrent"
                  className="bsInput"
                  type="number"
                  min={1}
                  value={maxConcurrentPerSlot}
                  onChange={handleMaxConcurrentChange}
                  onBlur={handleInputBlur}
                  onMouseDown={handleInputMouseDown}
                  onMouseUp={handleInputMouseUp}
                  onMouseLeave={handleInputMouseUp}
                  placeholder="e.g. 2"
                  disabled={saving || !allowDoubleBooking}
                  inputMode="numeric"
                />

                <div className="bsSummary">{getModeSummary()}</div>
              </div>
            </div>
          </section>

          <section className="bsCard" aria-label="Appointment status and check-in">
            <div className="bsCardBody">

              <div className="bsRow">
                <div style={{ minWidth: 260, flex: 1 }}>
                  <p className="bsRowTitle">Allow client self check-in</p>
                  <p className="bsRowDesc">
                    Clients can check in from their portal or via a link sent before their appointment.
                  </p>
                </div>
                <label className="bsCheckbox" aria-label="Allow client self check-in">
                  <input
                    type="checkbox"
                    checked={allowClientCheckIn}
                    onChange={handleAllowClientCheckInChange}
                    disabled={saving}
                  />
                </label>
              </div>

              <div className="bsDivider" />

              <div className="bsRow">
                <div style={{ minWidth: 260, flex: 1 }}>
                  <p className="bsRowTitle">Send check-in link automatically</p>
                  <p className="bsRowDesc">
                    Text and/or email clients a self check-in link before their appointment (timing matches the field below). Customize messages in SMS and email settings.
                  </p>
                </div>
                <label className="bsCheckbox" aria-label="Send check-in link automatically">
                  <input
                    type="checkbox"
                    checked={automatedCheckInReminderEnabled}
                    onChange={handleAutomatedCheckInReminderChange}
                    disabled={saving || !allowClientCheckIn}
                  />
                </label>
              </div>

              {automatedCheckInReminderEnabled && allowClientCheckIn && (
                <div className="bsChannelOptions">
                  <label className="bsCheckbox" aria-label="Send check-in reminder by SMS">
                    <input
                      type="checkbox"
                      checked={checkInReminderSendSms}
                      onChange={handleCheckInReminderSmsChange}
                      disabled={saving}
                    />
                    <span className="bsCheckboxLabel">SMS</span>
                  </label>
                  <label className="bsCheckbox" aria-label="Send check-in reminder by email">
                    <input
                      type="checkbox"
                      checked={checkInReminderSendEmail}
                      onChange={handleCheckInReminderEmailChange}
                      disabled={saving}
                    />
                    <span className="bsCheckboxLabel">Email</span>
                  </label>
                </div>
              )}

              <div className="bsField" style={{ marginTop: 12 }}>
                <label className="bsLabel" htmlFor="bs-checkin-window">
                  Check-in opens & reminder sends (minutes before appointment)
                </label>
                <input
                  id="bs-checkin-window"
                  className="bsInput"
                  type="number"
                  min={5}
                  max={240}
                  value={checkInWindowMinutesBefore}
                  onChange={(e) => setCheckInWindowMinutesBefore(e.target.value)}
                  onBlur={handleCheckInWindowBlur}
                  disabled={saving || !allowClientCheckIn}
                  inputMode="numeric"
                />
              </div>

              <div className="bsRow" style={{ marginTop: 12 }}>
                <div style={{ minWidth: 260, flex: 1 }}>
                  <p className="bsRowTitle">Require client confirmation before check-in</p>
                  <p className="bsRowDesc">
                    When enabled, clients must confirm attendance before they can self check-in.
                  </p>
                </div>
                <label className="bsCheckbox" aria-label="Require client confirmation before check-in">
                  <input
                    type="checkbox"
                    checked={requireClientConfirmedBeforeCheckIn}
                    onChange={handleRequireConfirmBeforeCheckInChange}
                    disabled={saving || !allowClientCheckIn}
                  />
                </label>
              </div>
            </div>
          </section>
          </>
        )}
      </div>
    </div>
  );
};

export default BookingSettings;


